import { Component } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AuthService } from "@ponder-core/auth/auth.service";
import { AuthStorage } from "@ponder-core/auth/auth.storage";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";

@Component({
    imports: [ReactiveFormsModule, BusySpinComponent],
    templateUrl: "./password-recovery.component.html"
})
export class PasswordRecoveryComponent {
    public recoveryForm!: FormGroup;
    public isGettingTemporaryAuth: boolean = true;
    public isProcessing: boolean = false;
    public isPasswordChangeValid: boolean = false;
    public invalidPasswordReasons: string[] = [];
    public authFailed: boolean = false;
    public processingError?: string;
    public emailAddress?: string;

    public constructor(formBuilder: FormBuilder, private events: EventBroker, private authService: AuthService, private router: Router) {
        this.recoveryForm = formBuilder.group({
            new: ["", [Validators.required, Validators.minLength(8), Validators.maxLength(64)]],
            confirm: ["", [Validators.required, Validators.minLength(8), Validators.maxLength(64)]]
        });

        this.recoveryForm.valueChanges.subscribe(() => { this.onPasswordChanged(); });
    }

    public ngOnDestroy(): void {
        this.events.ui.restoreContentPaddingEvent.emit();
    }

    public ngOnInit(): void {
        this.events.ui.hideNavbarEvent.emit();
        this.events.ui.removeContentPaddingEvent.emit();

        this.getTempAuthAsync(3).then(() => {
            this.isGettingTemporaryAuth = false;
        });
    }

    private async getTempAuthAsync(retries: number): Promise<void> {
        retries--;

        if (retries < 0) {
            this.authFailed = true;
            return;
        }

        var authSession = await this.authService.getSessionAsync();

        if (authSession) {
                this.emailAddress = authSession.email;
                AuthStorage.setTempSessionFlag();
        } else {
            await new Promise(f => setTimeout(f, 500));
            this.authService.resetSession();
            await this.getTempAuthAsync(retries);
        }
    }

    private onPasswordChanged(): void {
        this.isPasswordChangeValid = true;
        this.invalidPasswordReasons = [];

        var newPassword = this.recoveryForm.get("new")!.value as string | undefined;
        var confirmPassword = this.recoveryForm.get("confirm")!.value as string | undefined;

        if (newPassword?.length) {
            if (newPassword.length < 8 || newPassword.length > 64) {
                this.isPasswordChangeValid = false;
                this.invalidPasswordReasons.push("Password must be between 8 and 64 characters.");
            }

            if (confirmPassword?.length) {
                if (confirmPassword != newPassword) {
                    this.isPasswordChangeValid = false;
                    this.invalidPasswordReasons.push("Passwords do not match!");
                }
            }
        }
    }

    public canSubmit(): boolean {
        return this.recoveryForm.valid && this.isPasswordChangeValid && !this.isProcessing && (this.emailAddress != undefined);
    }

    public async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        this.isProcessing = true;
        this.processingError = undefined;
        this.recoveryForm.disable({ emitEvent: false });

        const result = await this.authService.resetPasswordAsync(this.recoveryForm.get("new")!.value);

        if (result[0]) {
            AuthStorage.clearTempSessionFlag();
            this.router.navigate(["/"]);
        } else {
            this.processingError = result[1];
            this.isProcessing = false;
            this.recoveryForm.enable({ emitEvent: false });
        }
    }
}